/**
 * useFlightStatusAutomation
 *
 * Runs inside FlightDetailScreen (TrackFlightView). Watches live FR24 data
 * and applies automated flight + quote status transitions:
 *
 *  POSITIONING / READY / DELAYED → IN_TRANSIT   (aircraft airborne, alt > 1000 ft)
 *  IN_TRANSIT  → COMPLETED                       (landed at expected destination)
 *  IN_TRANSIT  → DIVERTED                        (landed at different airport)
 *  Any pre-flight → DELAYED                      (departure time + 2 h passed)
 *
 * Quote status follows:
 *  Any flight IN_TRANSIT  → Quote IN_TRANSIT
 *  ALL flights COMPLETED  → Quote COMPLETED
 *  Any flight DIVERTED    → Quote DIVERTED
 *  ALL flights CANCELLED  → Quote CANCELLED
 */
import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { deriveQuoteStatus } from '@/features/track/utils/flightStatusUtils';

const PRE_FLIGHT = ['POSITIONING', 'READY', 'DELAYED'];
const AIRBORNE_ALT_FT = 1000;

export function useFlightStatusAutomation(flight, liveData) {
  const queryClient = useQueryClient();
  const transitioningRef = useRef(false);
  const appliedStatusRef = useRef(null); // prevent duplicate transitions
  const landingCheckActiveRef = useRef(false);

  // Reset per-render guards whenever the DB status updates
  useEffect(() => {
    appliedStatusRef.current = null;
    landingCheckActiveRef.current = false;
  }, [flight?.flight_status]);

  const applyTransition = useCallback(async (newStatus) => {
    if (!flight?.id || transitioningRef.current) return;

    const current = (flight.flight_status || '').toUpperCase();
    const target = newStatus.toUpperCase();
    if (current === target || appliedStatusRef.current === target) return;

    appliedStatusRef.current = target;
    transitioningRef.current = true;

    try {
      console.log(`[StatusAutomation] Flight ${flight.id}: ${current} → ${target}`);
      await apiClient.patchFlight(flight.id, { flight_status: target });

      // Cascade to quote
      const quoteId = flight.quote?.id || flight.quote_id;
      if (quoteId) {
        const newQuoteStatus = await deriveQuoteStatus(quoteId, flight.id, target);
        if (newQuoteStatus) {
          console.log(`[StatusAutomation] Quote ${quoteId}: → ${newQuoteStatus}`);
          await apiClient.updateQuote(quoteId, { status: newQuoteStatus });
        }
      }

      queryClient.invalidateQueries({ queryKey: ['flightDetails', flight.id] });
      queryClient.invalidateQueries({ queryKey: ['flights'] });
    } catch (err) {
      console.error('[StatusAutomation] Transition failed:', err);
      appliedStatusRef.current = null; // allow retry
    } finally {
      transitioningRef.current = false;
    }
  }, [flight?.id, flight?.flight_status, flight?.quote?.id, flight?.quote_id, queryClient]);

  // ── DELAYED: pure time check, no FR24 required ───────────────────────────
  useEffect(() => {
    if (!flight?.id || !flight.departure_date) return;
    const status = (flight.flight_status || '').toUpperCase();
    if (['DELAYED', 'IN_TRANSIT', 'COMPLETED', 'DIVERTED', 'LAYOVER', 'CANCELLED'].includes(status)) return;

    const delayedAt = new Date(flight.departure_date).getTime() + 2 * 60 * 60 * 1000;
    const msUntil = delayedAt - Date.now();

    if (msUntil <= 0) {
      applyTransition('DELAYED');
      return;
    }

    const t = setTimeout(() => applyTransition('DELAYED'), msUntil);
    return () => clearTimeout(t);
  }, [flight?.id, flight?.flight_status, flight?.departure_date, applyTransition]);

  // ── Live data transitions ─────────────────────────────────────────────────
  useEffect(() => {
    if (!liveData || !flight?.id) return;

    const status = (flight.flight_status || '').toUpperCase();
    const alt = liveData.alt ?? 0;
    const gspeed = liveData.gspeed ?? 0;
    const vspeed = liveData.vspeed ?? 0;

    // Pre-flight → IN_TRANSIT when airborne
    if (PRE_FLIGHT.includes(status)) {
      if (alt > AIRBORNE_ALT_FT) {
        // Verify route so we don't pick up a passing aircraft
        const liveOrig = (liveData.orig_icao || liveData.orig_iata || '').toUpperCase();
        const liveDest = (liveData.dest_icao || liveData.dest_iata || '').toUpperCase();
        const depAp = (flight.departure_airport || '').toUpperCase();
        const arrAp = (flight.arrival_airport || '').toUpperCase();

        const routeOk =
          !liveOrig || !liveDest || // can't verify — trust the altitude
          liveOrig === depAp ||
          liveDest === arrAp;

        if (routeOk) {
          landingCheckActiveRef.current = false;
          applyTransition('IN_TRANSIT');
        }
      }
      return;
    }

    // IN_TRANSIT → landing detection
    if (status === 'IN_TRANSIT') {
      const hasLanded = gspeed === 0 && vspeed === 0;
      if (hasLanded && !landingCheckActiveRef.current) {
        landingCheckActiveRef.current = true;
        confirmLanding(flight, liveData).then((transition) => {
          if (transition) applyTransition(transition);
          else landingCheckActiveRef.current = false; // check failed — retry on next tick
        });
      }
    }
  }, [liveData, flight?.id, flight?.flight_status, applyTransition]);
}

// ── Fetch FR24 summary to confirm COMPLETED vs DIVERTED ──────────────────────
async function confirmLanding(flight, liveData) {
  try {
    const tailNumber = flight.aircraft?.tail_number || liveData.reg;
    if (!tailNumber) return 'COMPLETED';

    const now = new Date();
    const from = new Date(now.getTime() - 12 * 60 * 60 * 1000).toISOString();
    const to = now.toISOString();

    const summaryData = await apiClient.getFlightSummary(tailNumber, from, to);
    const summaries = Array.isArray(summaryData) ? summaryData : (summaryData?.data || []);

    if (!summaries.length) {
      // Aircraft disappeared from radar — treat as landed at destination
      return 'COMPLETED';
    }

    const latest = summaries[summaries.length - 1];
    const landedAt = (latest?.dest_icao || latest?.dest_iata || '').toUpperCase();
    const expected = (flight.arrival_airport || '').toUpperCase();

    if (!landedAt || !expected) return 'COMPLETED';
    if (landedAt === expected) return 'COMPLETED';

    // Landed at a different airport
    // Note: LAYOVER (multi-leg intermediate stop) is resolved by the QuotesScreen
    // background sweep. Here we default to DIVERTED for single-leg flights.
    return 'DIVERTED';
  } catch (err) {
    console.error('[StatusAutomation] confirmLanding failed:', err);
    return null; // don't transition on error
  }
}

